import UploadProgressToast from "@/components/UploadProgress/UploadProgressToast";
import { startProfilingStatusPolling } from "@/pages/Catalogue/CatalogueForm/SecondStep/utils/profilingStatusUpdater";
import { UploadStatus } from "@/shared/enums/upload-status";
import { RootState } from "@/store";
import { artifactUploaderActions } from "@/store/artifactUploader/artifactUploaderAction";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useRouteNavigation } from "./hooks/useRouteNavigation";
import {
	filterVisibleUploads,
	useUploadCleanup,
} from "./hooks/useUploadCleanup";

const isInExcludedRoute = (path: string): boolean => {
	return (
		path.includes("/second-step/") ||
		path === "/dataset/new" ||
		(path.includes("/catalogue/") && path.endsWith("/2"))
	);
};

interface GlobalUploadProgressToastProps {
	autoHide?: boolean;
}

const GlobalUploadProgressToast: React.FC<GlobalUploadProgressToastProps> = ({
	autoHide = true,
}) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { activeUploads } = useSelector((state: RootState) => state.uploadFile);
	const [isVerifying, setIsVerifying] = React.useState(false);

	const isExcludedRoute = isInExcludedRoute(location.pathname);

	useRouteNavigation();

	useUploadCleanup({
		isInExcludedRoute: isExcludedRoute,
	});

	// Iniciar el polling global al montar el componente y cuando cambien los uploads
	useEffect(() => {
		const hasAnalyzingUploads = activeUploads.some(
			(upload) => upload.status === UploadStatus.ANALYZING,
		);

		if (hasAnalyzingUploads && !isExcludedRoute) {
			// Si hay archivos ANALYZING al navegar, verificar inmediatamente
			setIsVerifying(true);
			startProfilingStatusPolling();

			// Dar tiempo a que el polling actualice Redux (500ms es suficiente)
			const timer = setTimeout(() => {
				setIsVerifying(false);
			}, 500);

			return () => clearTimeout(timer);
		}
		setIsVerifying(false);
		startProfilingStatusPolling();
	}, [activeUploads, location.pathname, isExcludedRoute]);

	const handleCloseUploadToast = () => {
		dispatch(artifactUploaderActions.hideAllUploadsFromToast());
	};

	if (isExcludedRoute) {
		return null;
	}

	if (isVerifying) {
		return null;
	}

	const uploadsToShow = filterVisibleUploads(activeUploads);

	if (uploadsToShow.length === 0) {
		return null;
	}

	return (
		<UploadProgressToast
			uploads={uploadsToShow}
			onClose={handleCloseUploadToast}
			position="bottom-right"
			autoHide={autoHide}
		/>
	);
};

export default GlobalUploadProgressToast;
